import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@tanstack/react-query";
import { Lightbulb, Loader2, X, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Small button + modal for any authenticated user to submit a feature request.
 * Emails admin via Resend, stores in Mongo `feature_requests` collection.
 */
export default function FeatureRequestButton() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium" });

  const submit = useMutation({
    mutationFn: (body) => api.post("/feature-requests", body),
    onSuccess: () => {
      toast.success("Feature request sent — we'll review it soon.");
      setForm({ title: "", description: "", priority: "medium" });
      setOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Both title and description are required");
      return;
    }
    submit.mutate(form);
  };

  const field = "w-full bg-[#050505] border border-white/10 focus:border-[#FF3B30] outline-none px-3 py-2 text-[13px] text-white placeholder:text-zinc-600";
  const label = "block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1";

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="feature-request-btn"
        title="Request a feature"
        className="p-2 border border-white/10 hover:border-[#FF3B30] text-zinc-300 hover:text-[#FF3B30] transition-colors"
      >
        <Lightbulb className="w-4 h-4" />
      </button>

      {open && createPortal((
        <div
          className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => setOpen(false)}
          data-testid="feature-request-modal"
        >
          <div
            className="w-full max-w-lg bg-[#09090B] border border-white/10 p-6 my-auto max-h-[calc(100vh-2rem)] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF3B30] mb-1">// request a feature</div>
                <div className="font-display font-black text-2xl tracking-tight text-white">What should we build next?</div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="feature-request-close" className="p-1.5 border border-white/10 hover:border-white/30">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className={label}>Feature title *</label>
                <input
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="feature-request-title"
                  className={field}
                  placeholder="Bulk XLSX export of leads"
                />
              </div>

              <div>
                <label className={label}>Description *</label>
                <textarea
                  required
                  rows={5}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="feature-request-description"
                  className={`${field} resize-none leading-relaxed`}
                  placeholder="What problem are you trying to solve? How does it help you day-to-day?"
                />
              </div>

              <div>
                <label className={label}>Priority</label>
                <div className="flex gap-2">
                  {["low", "medium", "high"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setForm({ ...form, priority: p })}
                      data-testid={`feature-request-priority-${p}`}
                      className={`flex-1 py-2 text-[11px] font-mono uppercase tracking-widest border ${
                        form.priority === p
                          ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                          : "border-white/10 text-zinc-400 hover:border-white/30"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={submit.isPending}
                data-testid="feature-request-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-mono text-[12px] uppercase tracking-widest py-3 disabled:opacity-50"
              >
                {submit.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submit.isPending ? "Sending…" : "Send request"}
              </button>
            </form>
          </div>
        </div>
      ), document.body)}
    </>
  );
}
